if (!customElements.get('emme-personalization')) {
  customElements.define('emme-personalization', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-personalization-config]').textContent);
      this.form = this.querySelector('form');
      this.fields = Object.fromEntries(Array.from(this.querySelectorAll('[data-field]')).map(input => [input.dataset.field, input]));
      this.button = this.querySelector('[data-submit]');
      this.selector = this.form.querySelector('select[name="id"]');
      this.error = this.querySelector('[data-form-error]');
      this.success = this.querySelector('[data-success]');
      this.busy = false;
      this.money = new Intl.NumberFormat(this.dataset.locale, { style:'currency', currency:this.dataset.currency });
      this.form.addEventListener('input', () => {
        this.success.hidden = true;
        this.update();
      });
      this.form.addEventListener('change', event => {
        this.success.hidden = true;
        this.update();
        if (event.target.name.startsWith('option-')) {
          const variant = this.getVariant();
          if (variant) {
            this.selector.value = String(variant.id);
            this.selector.dispatchEvent(new CustomEvent('change', {bubbles:true, detail:variant}));
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            history.replaceState(history.state, '', url);
          }
        }
      });
      Object.entries(this.fields).forEach(([key, input]) => {
        input.addEventListener('blur', () => { if (input.required) this.validateField(key); });
      });
      this.form.addEventListener('submit', event => this.submit(event));
      this.querySelectorAll('[data-quantity-step]').forEach(button => {
        button.addEventListener('click', () => {
          const input = this.querySelector('[data-quantity]');
          const current = Number(input.value);
          input.value = String(Math.max(1, (Number.isSafeInteger(current) && current >= 1 ? current : 1) + Number(button.dataset.quantityStep)));
          input.dispatchEvent(new Event('input', {bubbles:true}));
        });
      });
      this.update();
    }
    getChoice() {
      return this.form.querySelector('[name="message-choice"]:checked').value;
    }
    getOptions() {
      return Array.from(this.querySelectorAll('[data-option-index]')).map(group => group.querySelector('input:checked')?.value);
    }
    getVariant() {
      const options = this.getOptions();
      return options.length ? this.config.variants.find(variant => variant.options.every((value, index) => value === options[index])) : this.config.variants[0];
    }
    message() {
      const choice = this.getChoice();
      return choice === 'default' ? this.config.defaultMessage : choice === 'custom' ? this.fields.message.value.trim() : '';
    }
    hasMini() {
      const mini = this.querySelector('[data-mini]');
      return !!mini && mini.querySelector('input:checked')?.value.toLowerCase() !== 'no mini';
    }
    update() {
      this.querySelector('[data-quantity-step="-1"]').disabled = Number(this.querySelector('[data-quantity]').value) <= 1;
      const choice = this.getChoice();
      this.querySelector('[data-custom-message]').hidden = choice !== 'custom';
      this.fields.message.required = choice === 'custom';
      this.fields.message.setAttribute('aria-required', String(choice === 'custom'));
      this.querySelector('[data-message-value]').value = this.message();
      this.querySelector('[data-color-details]').hidden = !this.hasMini();
      this.fields.color.disabled = !this.hasMini();
      Object.entries(this.fields).forEach(([key, input]) => {
        const counter = this.querySelector('[data-count="' + key + '"]');
        if (counter) counter.textContent = input.value.length + ' / ' + input.maxLength;
        if (input.value.trim() || !input.required) {
          input.removeAttribute('aria-invalid');
          const error = this.querySelector('[data-error="' + key + '"]');
          if (error) error.textContent = '';
        }
      });
      this.querySelector('[data-summary-front]').textContent = this.fields.name.value.trim() || this.config.strings.emptyName;
      this.querySelector('[data-summary-back]').textContent = [this.message() || (choice === 'none' ? this.config.strings.noStatement : ''), this.fields.contact.value.trim(), this.fields.second.value.trim()].filter(Boolean).join(' · ');
      const options = this.getOptions();
      this.querySelectorAll('[data-option-index]').forEach(group => {
        const index = Number(group.dataset.optionIndex);
        group.querySelectorAll('input').forEach(input => {
          const matching = this.config.variants.filter(variant => variant.options[index] === input.value && variant.options.every((value, optionIndex) => optionIndex === index || value === options[optionIndex]));
          input.disabled = !matching.some(variant => variant.available);
          input.closest('label').querySelector('[data-option-status]').textContent = input.disabled ? this.config.strings.soldOut : '';
        });
      });
      const variant = this.getVariant();
      this.button.disabled = this.busy || !variant?.available;
      this.button.textContent = this.busy ? this.config.strings.adding : !variant ? this.config.strings.unavailable : !variant.available ? this.config.strings.soldOut : this.config.strings.add + ' — ' + this.money.format(variant.price / 100);
      this.querySelector('[data-summary-variant-row]').hidden = !options.length;
      this.querySelector('[data-summary-variant]').textContent = variant?.title || this.config.strings.unavailable;
    }
    validateField(key) {
      const input = this.fields[key];
      const valid = !input.required || !!input.value.trim();
      input.setAttribute('aria-invalid', String(!valid));
      const error = this.querySelector('[data-error="' + key + '"]');
      if (error) error.textContent = valid ? '' : this.config.strings[key + 'Error'];
      return valid;
    }
    properties() {
      return {
        Name:this.fields.name.value.trim(),
        'Back Statement':this.message(),
        'Line 1':this.fields.contact.value.trim(),
        'Line 2':this.fields.second.value.trim(),
        'Mini Color Suggestion':this.hasMini() ? this.fields.color.value.trim() : '',
        '_Back Statement Choice':this.getChoice(),
        '_Personalization Template':'personalization-simple-v1'
      };
    }
    async submit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      this.error.hidden = true;
      this.success.hidden = true;
      const invalid = ['name','contact','message'].filter(key => !this.validateField(key));
      if (invalid.length) { this.fields[invalid[0]].focus(); return; }
      const variant = this.getVariant();
      if (!variant?.available) return;
      const quantityInput = this.querySelector('[data-quantity]');
      const quantity = Number(quantityInput.value);
      if (!Number.isSafeInteger(quantity) || quantity < 1) {
        this.error.textContent = 'Enter a whole-number quantity of at least 1.';
        this.error.hidden = false;
        quantityInput.focus();
        return;
      }
      this.busy = true;
      this.update();
      try {
        const response = await fetch(this.dataset.addUrl, {
          method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'},
          body:JSON.stringify({items:[{id:variant.id, quantity, properties:this.properties()}]})
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.config.strings.addError);
        this.success.hidden = false;
        this.success.focus();
        document.dispatchEvent(new CustomEvent('emme:cart-added', {bubbles:true, detail:{items:result.items}}));
        // Refresh the installed Slide Cart app after our native Ajax add.
        try { if (typeof window.SLIDECART_UPDATE === 'function') window.SLIDECART_UPDATE(); } catch {}
        this.refreshCartHeader();
      } catch (error) {
        this.error.textContent = error.message || this.config.strings.addError;
        this.error.hidden = false;
      } finally {
        this.busy = false;
        this.update();
      }
    }
    async refreshCartHeader() {
      try {
        const response = await fetch(this.dataset.cartUrl, {headers:{Accept:'text/html'}});
        if (!response.ok) return;
        const documentCopy = new DOMParser().parseFromString(await response.text(), 'text/html');
        document.querySelectorAll('#site-control .cart:not(.nav-search)').forEach((current, index) => {
          const replacement = documentCopy.querySelectorAll('#site-control .cart:not(.nav-search)')[index];
          if (replacement) current.replaceWith(replacement);
        });
      } catch {}
    }
  });
}
